// Rapports › Coûts IA › « Tous les appels » (owner, 2026-10-23: « where can I
// see the details of the AI calls? » → « yes add the list »). Every call,
// newest first: time, what it was for, model, the order or chat, tokens,
// duration, cost. Filters: feature, order, errors only; « Voir plus » pages
// back in time. Data: worker GET /dashboard/ai-calls.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { DateRange } from '@/components/admin/DateRangePicker';
import { dashboardApi, type AiCallRow } from '@/lib/dashboardApi';
import { AI_FEATURES, aiFeatureLabel } from '@/lib/aiFeatures';
import { useT } from '@/lib/i18n';
import { CARD_BORDER } from './home/homeParts';

function time(iso: string, lang: string): string {
  return new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Africa/Casablanca',
  });
}

function cost(r: AiCallRow, lang: string): string {
  if (r.mad == null) return '—';
  const v = r.mad;
  return `${v.toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR', { maximumFractionDigits: v < 0.1 ? 3 : 2 })} DH`;
}

function Who({ r }: { r: AiCallRow }) {
  if (r.order) {
    const label = `${r.order.name ?? r.order.shopifyOrderId ?? 'commande'}${r.order.customer ? ` · ${r.order.customer}` : ''}`;
    return r.order.shopifyOrderId
      ? <Link to={`/admin/orders/${encodeURIComponent(r.order.shopifyOrderId)}`} className="text-adm-gold-ink hover:underline truncate block" title={label}>{label}</Link>
      : <span className="truncate block" title={label}>{label}</span>;
  }
  if (r.chatId) {
    return (
      <Link to={`/admin/assistant?tab=conversations&chat=${encodeURIComponent(r.chatId)}`} className="text-adm-gold-ink hover:underline truncate block">
        {r.chatLabel ?? 'WhatsApp'}
      </Link>
    );
  }
  return <span className="text-adm-faint">—</span>;
}

export function AiCallsList({ range }: { range: DateRange }) {
  const { t, lang } = useT();
  const [feature, setFeature] = useState('');
  const [orderInput, setOrderInput] = useState('');
  const [order, setOrder] = useState('');
  const [errors, setErrors] = useState(false);

  const q = useInfiniteQuery({
    queryKey: ['ai-calls', range.from ?? null, range.to ?? null, feature, order, errors] as const,
    queryFn: ({ pageParam }) => dashboardApi.aiCalls({ ...range, feature: feature || undefined, order: order || undefined, errors, before: pageParam || undefined }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.next ?? undefined,
    staleTime: 30_000,
  });
  const rows = q.data?.pages.flatMap((p) => p.rows) ?? [];
  const notFound = q.data?.pages[0]?.orderNotFound;

  const input = 'h-8 rounded-lg border border-[#ECE5D6] dark:border-adm-line bg-white dark:bg-adm-surface px-2 text-[12.5px] text-adm-ink';
  return (
    <div className={`bg-white rounded-[14px] border ${CARD_BORDER} p-4 mt-3`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="text-[14px] font-bold text-adm-ink m-0 mr-auto">{t('Tous les appels')}</h3>
        <select aria-label={t('Fonction')} value={feature} onChange={(e) => setFeature(e.target.value)} className={input}>
          <option value="">{t('Toutes les fonctions')}</option>
          {AI_FEATURES.map((f) => <option key={f.slug} value={f.slug}>{t(f.label)}</option>)}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); setOrder(orderInput.trim()); }} className="flex gap-1">
          <input value={orderInput} onChange={(e) => setOrderInput(e.target.value)} placeholder={t('Commande, ex. #1256')}
            aria-label={t('Commande')} className={`${input} w-[150px]`} />
          {order && <button type="button" onClick={() => { setOrder(''); setOrderInput(''); }} className="text-[12px] text-adm-muted px-1">✕</button>}
        </form>
        <label className="flex items-center gap-1.5 text-[12.5px] text-adm-ink-2">
          <input type="checkbox" checked={errors} onChange={(e) => setErrors(e.target.checked)} />
          {t('Échecs seulement')}
        </label>
      </div>

      {q.isLoading && <p className="text-[12.5px] text-adm-muted">{t('Chargement…')}</p>}
      {q.isError && <p className="text-[12.5px] text-status-stuck">{t('Liste indisponible. Réessayez.')}</p>}
      {notFound && <p className="text-[12.5px] text-status-waiting">{t('Commande introuvable.')}</p>}
      {!q.isLoading && !q.isError && !notFound && rows.length === 0 && (
        <p className="text-[12.5px] text-adm-muted">{t('Aucun appel avec ces filtres sur la période.')}</p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-[12.5px] min-w-[760px]">
            <thead>
              <tr className="text-left text-[11px] text-adm-muted">
                <th className="font-medium py-1.5 pr-3">{t('Heure')}</th>
                <th className="font-medium py-1.5 pr-3">{t('Fonction')}</th>
                <th className="font-medium py-1.5 pr-3">{t('Commande / chat')}</th>
                <th className="font-medium py-1.5 pr-3">{t('Modèle')}</th>
                <th className="font-medium py-1.5 pr-3 text-right">{t('Jetons')}</th>
                <th className="font-medium py-1.5 pr-3 text-right">{t('Durée')}</th>
                <th className="font-medium py-1.5 text-right">{t('Coût')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#F3EEE3] dark:border-adm-line align-top">
                  <td className="py-1.5 pr-3 whitespace-nowrap text-adm-muted tabular-nums">{time(r.at, lang)}</td>
                  <td className="py-1.5 pr-3">
                    <span className="text-adm-ink">{t(aiFeatureLabel(r.feature))}</span>
                    {!r.ok && <span className="ml-1.5 text-[10.5px] font-bold text-status-stuck" title={r.error ?? undefined}>{t('échec')}</span>}
                    {r.kind && r.kind !== r.feature && <span className="block text-[10.5px] text-adm-faint">{r.kind}</span>}
                  </td>
                  <td className="py-1.5 pr-3 max-w-[220px]"><Who r={r} /></td>
                  <td className="py-1.5 pr-3 text-adm-ink-2 whitespace-nowrap" title={r.key === 'finder' ? t('clé « finder »') : undefined}>
                    {r.model.replace(/^[^/]+\//, '') || '—'}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-adm-muted whitespace-nowrap">
                    {r.tokensIn != null || r.tokensOut != null ? `${r.tokensIn ?? 0} → ${r.tokensOut ?? 0}` : '—'}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-adm-muted whitespace-nowrap">
                    {r.ms != null ? `${(r.ms / 1000).toFixed(1)} s` : '—'}
                  </td>
                  <td className="py-1.5 text-right tabular-nums whitespace-nowrap text-adm-ink"
                    title={r.costSource === 'missing' ? t('prix pas encore reçu d’OpenRouter') : r.usd != null ? `$${r.usd.toFixed(5)}` : undefined}>
                    {r.costSource === 'missing' ? <span className="text-status-waiting">{t('en attente')}</span> : cost(r, lang)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {q.hasNextPage && (
        <button type="button" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}
          className="mt-3 h-8 px-3 rounded-lg border border-[#ECE5D6] dark:border-adm-line text-[12.5px] font-semibold text-adm-ink-2 hover:text-adm-ink">
          {q.isFetchingNextPage ? t('Chargement…') : t('Voir plus')}
        </button>
      )}
    </div>
  );
}
